import { formatBytes } from "@/lib/ffmpegEngine";

/** Accepted on drop (see looksLikeVideo); shown under the drop zone. Native ffmpeg converts each to MP4 with the same settings. */
export const CONVERT_INPUT_NOTE = "MP4, MOV, MKV, AVI, WebM, WMV, FLV, 3GP and similar video files";

export interface ConvertSummaryInput {
  fileName: string;
  inBytes: number;
  outBytes: number;
  /** "mp4", "mp4-web", "webm" or "mov" */
  format: string;
  /** The streams were copied into the new container, not re-encoded. */
  rewrapped: boolean;
}

/** Percentage change, rounded; "+0%" is shown only when the size really is within half a percent. */
export function sizeChangePct(inBytes: number, outBytes: number): number {
  return inBytes > 0 ? Math.round(((outBytes - inBytes) / inBytes) * 100) : 0;
}

/**
 * One line under the result: real sizes (KB under 1 MB, so a 45 KB clip does not read "0.0 MB"), the change, and why.
 * A copied file explains its "+0%": the picture and sound were repackaged, so the size hardly moves.
 */
export function convertSummary(i: ConvertSummaryInput): string {
  const pct = sizeChangePct(i.inBytes, i.outBytes);
  const inExt = (i.fileName.match(/\.([^.]+)$/)?.[1] ?? "video").toUpperCase();
  const outLabel = i.format === "mp4-web" ? "MP4 (web)" : i.format.toUpperCase();
  const how = i.rewrapped
    ? "the picture and sound were copied into the new format without re-encoding, so the quality is unchanged and the size hardly moves"
    : "re-encoded";
  const inWebm = /webm$/i.test(i.fileName);
  const note = !i.rewrapped && pct > 25 && inWebm && i.format.startsWith("mp4") ? " MP4 (H.264) is often larger than WebM for the same picture quality — that is normal." : "";
  return `${inExt} ${formatBytes(i.inBytes)} → ${outLabel} ${formatBytes(i.outBytes)} (${pct >= 0 ? "+" : ""}${pct}%), ${how}.${note}`;
}

/** Shown when the chosen output is the format the file already has. */
export function sameFormatNote(fileName: string, format: string): string | null {
  const ext = (fileName.match(/\.([^.]+)$/)?.[1] ?? "").toLowerCase();
  const isMp4 = ext === "mp4" || ext === "m4v";
  if ((format === "mp4" || format === "mp4-web") && isMp4) return "This file is already an MP4. Both MP4 choices write the same fast-start MP4, so converting changes little; pick WebM or MOV if you need a different format.";
  if (format === "mov" && ext === "mov") return "This file is already a MOV. Pick MP4 for a file that plays on more devices.";
  if (format === "webm" && ext === "webm") return "This file is already a WebM. It would be re-encoded at lower quality for no gain; pick MP4 instead.";
  return null;
}
